<script setup lang="ts">
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { usePublicStore } from '../../store/publicStore';
import { POPUPS_ANIMATION } from '../../constants/popups';
import { useAppStore } from '../../store/appStore';
import { UserRole, UserRoleType } from '../../types/user_role';
import Button from '@/dashboard/vue/components/common/Button.vue';

const appStore = useAppStore();
const publicStore = usePublicStore();
const roles: UserRoleType[] = [UserRole.Owner];

const { currentWorkspaceUser, workspace } = storeToRefs(appStore);
const { showLimitPopup, bytesCanBeUsed } = storeToRefs(publicStore);
const limitPopupText = computed(
  () =>
    `Your current plan does not allow you to upload that much. You have the ability to upload up to ${formatBytes(bytesCanBeUsed.value ? bytesCanBeUsed.value : 0)}.`
);
const showLink = computed(() => currentWorkspaceUser.value && roles.includes(currentWorkspaceUser.value.role));

function formatBytes(bytes: number, decimals: number = 1) {
  if (!+bytes) return '0B';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))}${sizes[i]}`;
}

function okay() {
  publicStore.openLimitPopup(false, bytesCanBeUsed.value);
}
</script>

<template>
  <TransitionRoot :show="showLimitPopup" as="template">
    <Dialog class="fixed bottom-zero left-zero right-zero top-zero z-[5000]" @close="okay">
      <!-- The backdrop, rendered as a fixed sibling to the panel container -->
      <TransitionChild
        as="template"
        :enter="POPUPS_ANIMATION.background.enter"
        :enter-from="POPUPS_ANIMATION.background.enterFrom"
        :enter-to="POPUPS_ANIMATION.background.enterTo"
        :leave="POPUPS_ANIMATION.background.leave"
        :leave-from="POPUPS_ANIMATION.background.leaveFrom"
        :leave-to="POPUPS_ANIMATION.background.leaveTo"
      >
        <div class="inset-0 fixed bottom-zero left-zero right-zero top-zero h-full w-full bg-gray-250/60 dark:bg-black/60" aria-hidden="true" />
      </TransitionChild>

      <!-- Full-screen container to center the panel -->
      <TransitionChild
        as="template"
        :enter="POPUPS_ANIMATION.content.enter"
        :enter-from="POPUPS_ANIMATION.content.enterFrom"
        :enter-to="POPUPS_ANIMATION.content.enterTo"
        :leave="POPUPS_ANIMATION.content.leave"
        :leave-from="POPUPS_ANIMATION.content.leaveFrom"
        :leave-to="POPUPS_ANIMATION.content.leaveTo"
      >
        <div class="absolute h-full w-full overflow-auto p-[47px]">
          <div class="flex min-h-full flex-1 items-center justify-center">
            <DialogPanel class="w-[520px] rounded-16 bg-white px-s pb-s pt-xl text-center dark:bg-gray-900 max-tablet:px-s">
              <h5 class="mb-s text-black dark:text-gray-400">Cloud reached</h5>
              <p class="text-content-m mb-[32px] px-l text-gray-900 dark:text-gray-500">
                {{ limitPopupText }}
              </p>

              <div class="flex justify-between gap-xs">
                <Button class="button-ghost h-[40px] max-w-[138px]" @click="() => okay()">
                  <span class="button-text">Ok</span>
                </Button>
                <router-link v-focus :to="`/w/${workspace?.short_link}`" class="button button-colored h-[40px] max-w-[138px]" @click="() => okay()">
                  <span class="button__text">All plans</span>
                </router-link>
              </div>
            </DialogPanel>
          </div>
        </div>
      </TransitionChild>
    </Dialog>
  </TransitionRoot>
</template>
